import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Userpilot } from 'userpilot'
import { BillingSummaryEmptyIllustration, Lock } from '@/assets/icons'
import { matterPaths } from '@/routing/paths'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import MatterRecordEmptyState from '@/matter-asset-common/asset/MatterRecordEmptyState'
import MatterRecordReadOnlyNotice from '@/matter-asset-common/asset/MatterRecordReadOnlyNotice'
import { selectMatterById } from '@/matter/store/selectors'
import { canEditMedicalBills } from '@/matter/utils/matter-permissions'
import { useMatterOwnerName } from '@/matter/hooks/useMatterOwnerName'
import { selectAddFilesStagingCountsForMatter, selectInitializeError } from '../store/selectors'
import { MedSummaryAssetActions } from '../store/slice'
import { kMedSummaryOwnerOnlyBuildMessage, kMedSummaryOwnerOnlyBuildOwnerMessage } from '../constants'

interface MedSummaryAssetEmptyStateProps {
  matterId: string
}

/**
 * Blank state shown when the matter has no billing summary yet. Thin wrapper over the shared
 * MatterRecordEmptyState that supplies the billing-summary copy, icon, CTA, and the
 * first-generation-error banner (shown after a failed Generate).
 */
export default function MedSummaryAssetEmptyState(props: MedSummaryAssetEmptyStateProps) {
  const { matterId } = props
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const initializeError = useAppSelector((state: RootState) => selectInitializeError(state, { matterId }))
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId }))
  // Read-only collaborators can't initiate a build — keep the CTA visible but disabled with an
  // explanatory tooltip. Gate on a loaded matter so the disabled state doesn't flash for owners.
  const isReadOnly = !!matter && !canEditMedicalBills(matter)
  const ownerName = useMatterOwnerName(matterId)
  const stagingCounts = useAppSelector((state: RootState) => selectAddFilesStagingCountsForMatter(state, { matterId }))
  const stagingTotal = stagingCounts.pendingCount + stagingCounts.stagedCount

  // Re-run Userpilot once the empty-state lander has mounted so a feature-launch
  // announcement can match this page. The global route-change reload (routes/root.tsx)
  // fires on navigation, before this lander finishes its async asset load and paints.
  useEffect(() => {
    Userpilot.reload()
  }, [])

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Owner-attribution notice: only shown in the pre-build empty state. Once a summary exists,
          the read-only treatment moves to the tabs-row "Viewing" pill. */}
      {isReadOnly && (
        <div className="pt-4 px-4">
          <MatterRecordReadOnlyNotice
            icon={<Lock />}
            headline={ownerName ? `${ownerName} is the owner of this matter.` : undefined}
            message={ownerName ? kMedSummaryOwnerOnlyBuildOwnerMessage : kMedSummaryOwnerOnlyBuildMessage}
          />
        </div>
      )}
      <MatterRecordEmptyState
        illustration={<BillingSummaryEmptyIllustration className="h-10 w-[63px] shrink-0" />}
        title="Build your medical billing summary"
        body="Build an itemized, citation-backed summary of charges, adjustments, and payments from your medical bills — every amount linked to its source page."
        ctaLabel="Add Files"
        onCtaClick={() => navigate(matterPaths.billing.assetAddFiles(matterId))}
        ctaDisabled={isReadOnly}
        ctaTooltip={isReadOnly ? kMedSummaryOwnerOnlyBuildMessage : undefined}
        notice={
          stagingTotal > 0
            ? `${stagingTotal} ${stagingTotal === 1 ? 'file' : 'files'} ${stagingCounts.pendingCount > 0 ? 'uploading' : 'ready'} — continue in Add Files`
            : undefined
        }
        error={
          initializeError
            ? {
                headline: 'Billing summary could not be generated.',
                detail: 'Please try again.',
                dismissLabel: 'Dismiss',
                onDismiss: () => dispatch(MedSummaryAssetActions.setInitializeError({ matterId, error: null })),
              }
            : undefined
        }
      />
    </div>
  )
}
